import { ConfiguratorState } from '@/app/store/configurator/types/configurator.types';
import i18next from 'i18next';
import {
	BORDER_WIDTH,
	LARGE_BORDER_WIDTH,
	RACK_MATERIALS_OPTIONS,
	SHELF_DECK,
	SIDES_DISTANCE,
} from '../constants/configurator';
import { Section } from '../types/configurator';

export const calculateConfiguratorWidth = (sections: Section[], wallMounted: boolean) => {
	if (wallMounted) {
		const width = sections.reduce((acc, section, index) => {
			const leftBorderWidth = index === 0 ? LARGE_BORDER_WIDTH : BORDER_WIDTH;
			const rightBorderWidth = index === sections.length - 1 ? LARGE_BORDER_WIDTH : 0;
			const sectionWidth = section.width + leftBorderWidth + rightBorderWidth;

			return acc + sectionWidth;
		}, 0);

		return width;
	} else {
		const width = sections.reduce((acc, section) => {
			const sectionWidth = section.width + LARGE_BORDER_WIDTH * 2;

			return acc + sectionWidth;
		}, 0);

		return width;
	}
};

export const calculateConfiguratorDepth = (sections: Section[], doubleSided: boolean) => {
	const maxDepth = Math.max(...sections.map((section) => section.depth), 0);
	if (!doubleSided) {
		return maxDepth;
	}

	return maxDepth * 2 + SIDES_DISTANCE;
};

export const calculateConfiguratorHeight = (sections: Section[]) => {
	const maxHeight = Math.max(...sections.map((section) => section.height), 0);
	return maxHeight;
};

export const calculateRackDepth = (sections: Section[]) => {
	const maxDepth = Math.max(...sections.map((section) => section.depth), 0);
	return maxDepth;
};

export const getMaxUsedValues = <T extends number | string>(values: T[]) => {
	if (!values.length) {
		return;
	}

	const usedValues = values.reduce<Record<T, number>>(
		(acc, value) => {
			if (acc[value]) {
				acc[value]++;
			} else {
				acc[value] = 1;
			}

			return acc;
		},
		{} as Record<T, number>,
	);
	const [maxUsedOption] = Object.entries(usedValues).sort((a, b) => {
		const aPriority = a[1] as number;
		const bPriority = b[1] as number;

		return bPriority - aPriority;
	})[0];

	return maxUsedOption as T;
};

export const getConfiguratorTitle = (state: ConfiguratorState) => {
	const materialLabel =
		RACK_MATERIALS_OPTIONS.find((option) => option.value === state.material)?.label || '';
	const stateLabel = i18next.t(materialLabel.replace('rack', 'rackState'));

	const height = calculateConfiguratorHeight(state.sections);
	const depth = calculateConfiguratorDepth(state.sections, state.doubleSided);
	const width = calculateConfiguratorWidth(state.sections, state.wallMounted);

	const levelsCount = Array.from(new Set(state.sections.map((section) => section.selfs.length)));
	const maxLevelsCount = Math.max(...levelsCount);
	const minLevelsCount = Math.min(...levelsCount);

	const shelfMaterials = Array.from(
		new Set(state.sections.flatMap((section) => section.selfs.map((shelf) => shelf.deck))),
	);
	const shelfMaterialsLabels = shelfMaterials.map(
		(material) => SHELF_DECK.find((option) => option.value === material)?.label,
	);

	const title = i18next.t('title');
	const heightLabel = `${i18next.t('units.height')} - ${height} ${i18next.t('units.mm')}`;
	const widthLabel = `${i18next.t('units.width')} - ${width} ${i18next.t('units.mm')}`;
	const depthLabel = `${i18next.t('units.depth')} - ${depth} ${i18next.t('units.mm')}`;
	const levelsLabels = `${minLevelsCount} ${maxLevelsCount !== minLevelsCount ? `- ${maxLevelsCount}` : ''} ${getLevelsLabel(minLevelsCount, maxLevelsCount)}`;
	const shelfLabel = `${i18next.t('sectionParams.shelfMaterial').toLowerCase()} - ${shelfMaterialsLabels.map((label) => i18next.t(label)).join(' / ')}`;

	return `${title} ${stateLabel} PIN Special ${heightLabel}, ${depthLabel}, ${widthLabel}, ${levelsLabels}, ${shelfLabel}`;
};

export const getLevelsLabel = (minLevelsCount: number, maxLevelsCount: number) => {
	if (minLevelsCount !== maxLevelsCount) {
		return i18next.t('units.levels.many');
	}

	if (maxLevelsCount < 5) return i18next.t('units.levels.two');

	return i18next.t('units.levels.many');
};
